import * as React from 'react';
import { useEffect, useState } from 'react';
import IconButton from '@mui/joy/IconButton';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Button from "@mui/joy/Button";
import Box from "@mui/joy/Box";
import { Close, Done, KeyboardArrowLeft, KeyboardArrowRight, Message, PhoneCallback, TuneRounded } from "@mui/icons-material";
import { CircularProgress, Chip, Select } from "@mui/joy";
import Option from "@mui/joy/Option";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useSearchParams } from "react-router-dom";
import BookingsDrawer from "../modals/BookingsDrawer";
import DebounceInput from "../../domain/DebounceInput";
import { useCall } from "../../lib/call-context";
import ApiService from "../../axios/Bookings-Axios";
import { GetUser } from "../../axios/Axios";
import {toast, ToastContainer} from "react-toastify";
import { normalizeUkPhone, ukPhoneVariants } from "../../lib/phone";
import ResponsiveTableWrapper, { ResponsiveFilterBar, FilterBarLeft, FilterBarRight } from "../ui/ResponsiveTableWrapper";
import AISentimentIcon, { SentimentData } from "../ui/AISentimentIcon";

// Define keyframes for the highlight animation
const highlightAnimation = `
  @keyframes highlight-row-animation {
    from {
      background-color: rgba(76, 175, 80, 0.3); /* Semi-transparent green */
    }
    to {
      background-color: transparent;
    }
  }

  /* Apply the animation to all direct child td elements of the highlighted row */
  .highlight-row > td {
    animation: highlight-row-animation 2s ease-out;
  }
`;

interface Row {
    id: string,
    booked_in: string,
    created_at: string,
    is_recurring: boolean,
    gutter_clean: boolean,
    conservatory_gutter_clean: boolean,
    roof_clean: boolean,
    status: string,
    job_status: string | null,
    cancellation_reason: string | null,
    cancelled_at: string | null,
    readable_services: string,
    expanded_row: RowExpanded,
    total_cost: number,
    franchise_location: string,
    referral_badge_label?: string,
    is_referral_reward?: boolean,
    is_referred_booking?: boolean,
}

interface RowExpanded {
    full_name: string,
    street_address: string,
    postcode: string,
    phone: string,
    notes: string,
    house_type: string,
    id: string,
    email: string,
    latest_sentiment: SentimentData | null,
    customer_created_at: string,
}

// Helper function to determine if customer uses new pricing structure
// New pricing: customer created on or after Feb 3rd, 2026
const isNewPricing = (customerCreatedAt: string): boolean => {
    if (!customerCreatedAt) return false;
    return new Date(customerCreatedAt) >= new Date('2026-02-03');
};

interface User {
    is_superuser: boolean;
    location_permissions: string[];
}

function formatDate(old_date: string | Date): string {
    const date = new Date(old_date);
    if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            hour: 'numeric', minute: 'numeric'
        });
    }
    return 'No Valid Date';
}

function Row(props: { row: Row; initialOpen?: boolean, onRowClick: (rowId: number) => void, isHighlighted: boolean, showAISentiment: boolean, showFranchiseLocation: boolean }) {
    const { row, isHighlighted, showAISentiment } = props;
    const [open, setOpen] = useState(props.initialOpen || false);
    const { makeCall } = useCall();

    const handleClick = () => props.onRowClick(Number(row.id));

    const handleCallCustomer = async () => {
        if (row.expanded_row.phone) {
            makeCall(row.expanded_row.phone, row.expanded_row.full_name);
        } else {
            toast.error("Phone number not available", { containerId: "bookings-table" });
        }
    };

    return (
        <React.Fragment>
            <tr className={isHighlighted ? 'highlight-row' : undefined}>
                <td><IconButton aria-label="expand row" variant="plain" color="neutral" size="sm" onClick={() => setOpen(!open)}>{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton></td>
                {showAISentiment && (
                    <td style={{ width: 40, textAlign: 'center' }}>
                        <AISentimentIcon sentiment={row.expanded_row.latest_sentiment} showBookingConfirmation={true} />
                    </td>
                )}
                <td>{formatDate(row.created_at)}</td>
                <td>{formatDate(row.booked_in)}</td>
                {props.showFranchiseLocation && <td>{row.franchise_location}</td>}
                <td>{row.expanded_row.full_name}</td>
                <td>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span>{row.readable_services}</span>
                        {row.referral_badge_label && (
                            <Chip size="sm" variant="soft" color="primary">
                                {row.referral_badge_label}
                            </Chip>
                        )}
                    </Box>
                </td>
                <td>
                    {row.job_status ? (
                        <Chip
                            size="sm"
                            color={
                                row.job_status === 'Mark As Complete' ? 'success' :
                                row.job_status === 'Mark As Cancelled' ? 'danger' :
                                row.job_status === 'Mark As Needs Rescheduling' ? 'warning' : 'neutral'
                            }
                        >
                            {row.job_status.replace('Mark As ', '')}
                        </Chip>
                    ) : (
                        <Chip size="sm" color="neutral">Pending</Chip>
                    )}
                </td>
                <td>
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                        <span>£{row.total_cost}</span>
                        {row.is_referral_reward && <Chip size="sm" color="primary">Free</Chip>}
                    </Box>
                </td>
                <td>
                    <Chip 
                        size="sm" 
                        variant="soft" 
                        color={isNewPricing(row.expanded_row.customer_created_at) ? 'success' : 'neutral'}
                    >
                        {isNewPricing(row.expanded_row.customer_created_at) ? 'NEW' : 'OLD'}
                    </Chip>
                </td>
                <td>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button sx={{ textWrap: 'nowrap' }} size="sm" color="primary" onClick={handleClick}>Edit Booking</Button>
                        <IconButton variant="solid" sx={{ ml: 1 }} size="sm" color="primary" onClick={handleCallCustomer}><PhoneCallback /></IconButton>
                    </Box>
                </td>
            </tr>
            <tr>
                <td style={{ height: 0, padding: 0 }} colSpan={(showAISentiment ? 10 : 9) + (props.showFranchiseLocation ? 1 : 0)}>
                    {open && (
                        <Sheet variant="soft" sx={{ p: 1, pl: 6, boxShadow: 'inset 0 3px 6px 0 rgba(0 0 0 / 0.08)' }}>
                            <Typography level="body-lg" component="div">Customer Information</Typography>
                            <Table borderAxis="bothBetween" aria-label="customer details" sx={{ '& > thead > tr > th:nth-child(n + 3), & > tbody > tr > td:nth-child(n + 3)': { textAlign: 'right' }, '--TableCell-paddingX': '0.5rem' }}>
                                <thead><tr><th>Full Name</th><th>Number</th><th>Email</th><th>Street Address</th><th>Postcode</th></tr></thead>
                                <tbody><tr><td>{row.expanded_row.full_name}</td><td>{row.expanded_row.phone}</td><td>{row.expanded_row.email}</td><td>{row.expanded_row.street_address}</td><td>{row.expanded_row.postcode}</td></tr></tbody>
                            </Table>
                        </Sheet>
                    )}
                </td>
            </tr>
            <tr>
                <td style={{ height: 0, padding: 0 }} colSpan={(showAISentiment ? 10 : 9) + (props.showFranchiseLocation ? 1 : 0)}>
                    {open && (
                        <Sheet variant="soft" sx={{ p: 1, pl: 6, boxShadow: 'inset 0 3px 6px 0 rgba(0 0 0 / 0.08)' }}>
                            <Table borderAxis="bothBetween" aria-label="notes">
                                <thead><tr><th>Notes</th></tr></thead>
                                <tbody><tr><td style={{ whiteSpace: 'pre-wrap' }}>{row.expanded_row.notes || 'No notes'}</td></tr></tbody>
                            </Table>
                        </Sheet>
                    )}
                </td>
            </tr>
            {row.job_status === 'Mark As Cancelled' && row.cancellation_reason && (
                <tr>
                    <td style={{ height: 0, padding: 0 }} colSpan={(showAISentiment ? 10 : 9) + (props.showFranchiseLocation ? 1 : 0)}>
                        {open && (
                            <Sheet variant="soft" sx={{ p: 1, pl: 6, boxShadow: 'inset 0 3px 6px 0 rgba(0 0 0 / 0.08)' }}>
                                <Table borderAxis="bothBetween" aria-label="cancellation-reason">
                                    <thead><tr><th>Cancellation Reason</th>{row.cancelled_at && <th>Cancelled On</th>}</tr></thead>
                                    <tbody><tr><td style={{ whiteSpace: 'pre-wrap' }}>{row.cancellation_reason}</td>{row.cancelled_at && <td>{formatDate(row.cancelled_at)}</td>}</tr></tbody>
                                </Table>
                            </Sheet>
                        )}
                    </td>
                </tr>
            )}
        </React.Fragment>
    );
}

const mapRawDataToRow = (data: any[]): Row[] => {
    return data.map(item => {
        const serviceNames = item.services.map((service: any) => service.name.toLowerCase());
        return {
            id: item.id.toString(),
            booked_in: item.booked_in,
            created_at: item.created_at,
            status: item.status,
            job_status: item.job_status || null,
            cancellation_reason: item.cancellation_reason || null,
            cancelled_at: item.cancelled_at || null,
            is_recurring: item.is_recurring,
            gutter_clean: serviceNames.includes('gutter clean'),
            conservatory_gutter_clean: serviceNames.includes('conservatory gutter clean'),
            roof_clean: serviceNames.includes('roof clean'),
            total_cost: item.total_cost,
            franchise_location: item.customer?.franchise_postcode?.franchise?.location || '',
            readable_services: item.readable_services,
            referral_badge_label: item.referral_badge_label || '',
            is_referral_reward: !!item.is_referral_reward,
            is_referred_booking: !!item.is_referred_booking,
            expanded_row: {
                full_name: item.customer.full_name,
                street_address: item.customer.street_address,
                postcode: item.customer.postcode,
                phone: item.customer.phone,
                notes: item.customer.notes,
                house_type: item.customer.house_type || '',
                id: item.customer.id.toString(),
                email: item.customer.email,
                latest_sentiment: item.customer.latest_sentiment || null,
                customer_created_at: item.customer.created_at || '',
            },
        };
    });
};

function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number; }) {
    return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
}

export default function BookingsTable() {
    const [loadedData, setLoadingData] = useState(false);
    const [rows, setRows] = useState<Row[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pageCount, setPageCount] = useState(0);
    const [search, setSearch] = useState('');
    const [jobStatusFilter, setJobStatusFilter] = useState<string | null>(null);
    const [urlSearchParam, setUrlSearchParam] = useSearchParams();
    const [openBookingId, setOpenBookingId] = useState(0);
    const [openBookingModal, setOpenBookingModal] = useState(false);
    const [openNewBookingId, setOpenNewBookingId] = useState(-1);
    const [highlightedRowId, setHighlightedRowId] = useState<number | null>(null);
    const { makeCall, call, incomingCall } = useCall();
    const [activeCallRow, setActiveCallRow] = useState<Row | null>(null);
    const [user, setUser] = useState<User>({ is_superuser: false, location_permissions: [] });
    const selectedLocation = localStorage.getItem('location') || '';
    const showFranchiseLocation = selectedLocation.toLowerCase() === 'all';

    // Fetch user data on mount
    useEffect(() => {
        const fetchUser = async () => {
            try {
                const userResponse = await GetUser();
                setUser(userResponse.data);
            } catch (error) {
                console.error("Failed to fetch user:", error);
            }
        };
        fetchUser();
    }, []);

    useEffect(() => {
        let cancelled = false;
        const remoteNumber = incomingCall?.parameters.From || call?.parameters.To;
        const targetNorm = remoteNumber ? normalizeUkPhone(remoteNumber) : null;

        if (!remoteNumber || !targetNorm) {
            setActiveCallRow(null);
            return () => { cancelled = true; };
        }

        const terms = ukPhoneVariants(remoteNumber);

        (async () => {
            for (const term of terms) {
                try {
                    const resp = await ApiService.getBookings({ search: term, page: 1 });
                    const results = resp?.results || [];
                    if (!results.length) continue;

                    const mapped = mapRawDataToRow(results);
                    const exact = mapped.filter((r) => normalizeUkPhone(r.expanded_row.phone) === targetNorm);
                    if (!exact.length) continue;

                    // Pick latest CUSTOMER (highest customer id) among exact phone matches
                    const best = exact
                        .slice()
                        .sort((a, b) => Number(b.expanded_row.id) - Number(a.expanded_row.id))[0];

                    if (!cancelled) setActiveCallRow(best || null);
                    return;
                } catch {
                    // try next term
                }
            }
            if (!cancelled) setActiveCallRow(null);
        })();

        return () => { cancelled = true; };
    }, [call, incomingCall]);

    const fetchData = async () => {
        setLoadingData(false);
        try {
            const params: any = { page: page + 1, search };
            if (jobStatusFilter) {
                params.job_status = jobStatusFilter;
            }
            const bookingsData = await ApiService.getBookings(params);
            setRows(mapRawDataToRow(bookingsData.results || []));
            setPageCount(bookingsData.count || 0);
        } catch (error) {
            console.error("Failed to fetch bookings:", error);
            setRows([]);
            setPageCount(0);
        } finally {
            setLoadingData(true);
        }
    };

    const handleCallback = (updateInfo?: { id: number }) => {
        setOpenBookingModal(false);
        if (updateInfo && updateInfo.id) {
            if (updateInfo.id !== -1) {
                setHighlightedRowId(updateInfo.id);
            }
            fetchData().then(() => {
                if (updateInfo.id !== -1) {
                    setTimeout(() => {
                        setHighlightedRowId(null);
                    }, 2000);
                }
            });
        }
    };

    const handleOpenBookingModal = (rowId: number) => {
        setOpenNewBookingId(-1);
        setOpenBookingModal(true);
        setOpenBookingId(rowId);
    };
    const handleNewOpenBookingModal = () => {
        setOpenBookingId(-1);
        setOpenNewBookingId(-1);
        setOpenBookingModal(true);
    };

    useEffect(() => {
        fetchData();
    }, [page, search, jobStatusFilter]);

    const handleDebounce = (value: string) => {
        setPage(0);
        setSearch(value);
    };

    const handleChangeStatusFilter = (event: React.SyntheticEvent | null, newValue: string | null) => {
        setPage(0);
        setJobStatusFilter(newValue);
    };

    const getLabelDisplayedRowsTo = () => {
        return Math.min(pageCount, (page + 1) * rowsPerPage);
    };

    return (
        <>
            <style>{highlightAnimation}</style>
            <ToastContainer containerId={"bookings-table"} position="top-right" autoClose={5000} theme="dark" />

            <ResponsiveFilterBar>
                <FilterBarLeft>
                    <DebounceInput 
                        sx={{ width: { xs: '100%', sm: '250px' } }} 
                        startDecorator={<SearchRoundedIcon />}
                        placeholder="Search by any field..."
                        debounceTimeout={1000}
                        defaultValue={urlSearchParam.get("booking-id") as string}
                        handleDebounce={handleDebounce}
                    />
                    <Select
                        placeholder="Filter by status..."
                        value={jobStatusFilter}
                        onChange={handleChangeStatusFilter}
                        startDecorator={<TuneRounded />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', gap: '0.25rem' }}>
                                <Chip variant="soft" color="primary">{selected?.label}</Chip>
                            </Box>
                        )}
                        sx={{ minWidth: { xs: '100%', sm: '15rem' } }}
                    >
                        <Option value="">All Bookings</Option>
                        <Option value="Mark As Complete">Completed</Option>
                        <Option value="Mark As Cancelled">Cancelled</Option>
                        <Option value="Mark As Needs Rescheduling">Needs Rescheduling</Option>
                    </Select>
                </FilterBarLeft>
                <FilterBarRight>
                    <Button onClick={handleNewOpenBookingModal}>Create New Booking</Button>
                </FilterBarRight>
            </ResponsiveFilterBar>

            {/* Active Call UI removed per request */}

            <ResponsiveTableWrapper minWidth={900}>
                {(() => {
                    const isSuperAdmin = (user.location_permissions || []).includes('super_admin');
                    const colCount = (isSuperAdmin ? 10 : 9) + (showFranchiseLocation ? 1 : 0);
                    return (
                        <Table hoverRow aria-label="collapsible table">
                            <thead>
                            <tr>
                                <th style={{ width: 40 }} aria-label="empty" />
                                {isSuperAdmin && <th style={{ width: 40 }} aria-label="AI Insights" title="AI Insights" />}
                                <th style={{ width: '15%' }}>Created At</th>
                                <th style={{ width: '15%' }}>Time Booked In</th>
                                {showFranchiseLocation && <th>Franchise</th>}
                                <th>Customer Name</th>
                                <th>Services</th>
                                <th>Status</th>
                                <th style={{ width: '90px' }}>Total Cost</th>
                                <th style={{ width: '70px' }}>Pricing</th>
                                <th style={{ width: '180px' }} aria-label="last" />
                            </tr>
                            </thead>
                            <tbody>
                            {loadedData ? (
                                rows.map((row) => (
                                    <Row
                                        key={row.id}
                                        row={row}
                                        initialOpen={search.length > 0}
                                        onRowClick={handleOpenBookingModal}
                                        isHighlighted={Number(row.id) === highlightedRowId}
                                        showAISentiment={isSuperAdmin}
                                        showFranchiseLocation={showFranchiseLocation}
                                    />
                                ))
                            ) : (
                                <tr><td colSpan={colCount} style={{ textAlign: 'center', padding: '2rem' }}><CircularProgress /></td></tr>
                            )}
                            </tbody>
                            <tfoot>
                            <tr>
                                <td colSpan={colCount}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                                        <Typography textAlign="center" sx={{ minWidth: 80 }}>
                                            {labelDisplayedRows({
                                                from: pageCount === 0 ? 0 : page * rowsPerPage + 1,
                                                to: getLabelDisplayedRowsTo(),
                                                count: pageCount,
                                            })}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton size="sm" color="neutral" variant="outlined" disabled={page === 0} onClick={() => setPage(page - 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowLeft /></IconButton>
                                            <IconButton size="sm" color="neutral" variant="outlined" disabled={page >= Math.ceil(pageCount / rowsPerPage) - 1} onClick={() => setPage(page + 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowRight /></IconButton>
                                        </Box>
                                    </Box>
                                </td>
                            </tr>
                            </tfoot>
                        </Table>
                    );
                })()}
            </ResponsiveTableWrapper>
            <React.Fragment>
                <BookingsDrawer key={'bookings-modal-' + openBookingModal} openState={openBookingModal}
                                newBooking={null}
                                newBookingValue={null}
                                bookingId={openBookingId} parentCallback={handleCallback} />
            </React.Fragment>

        </>
    );
}